import { Component, Inject, OnInit, Renderer2 } from "@angular/core";
import { GlobalService } from "./shared/services/global.service";
import { ActivationStart, Router } from "@angular/router";
import { HtmlMetaService } from "./shared/services/html-meta.service";
import { ModalService } from "./shared/services/modal.service";
import { AuthenticationService } from "./shared/services/authentication.service";
import { UserService } from "./shared/services/user.service";
import { StudentService } from "./shared/services/student.service";
import { empty } from "./functions";
import { RoleTypes } from "./guards/roletypes";
import { catchError, map, startWith } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { Meta } from "@angular/platform-browser";
import { DomainService } from "./shared/services/domain.service";
import { LocaleService } from "./shared/services/locale.service";
import { DOCUMENT } from "@angular/common";
import { SentryFeedbackService } from "./shared/services/sentry-feedback.service";
import { CurrentUserStore } from "./shared/stores/current-user.store";

@Component({
  selector: "app-root",
  template: `
    <!-- Loading Screen: Show while authentication initializes -->
    <div *ngIf="showLoading$ | async" class="wrapper-app-loader">
      <img
        class="loader-logo"
        src="assets/img/header-logo.png"
        alt="Loading..."
      />
      <div class="app-loader-mty">
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>

    <!-- App Content: Only show after authentication is complete -->
    <ng-container *ngIf="(showLoading$ | async) === false">
      <app-alert class="app-alert"></app-alert>
      <router-outlet></router-outlet>
    </ng-container>
  `,
  standalone: false,
})
export class AppComponent implements OnInit {
  title = "Meet the Youngsters";

  /**
   * Observable that controls visibility of the loading screen
   * Shows loading screen while authentication is initializing
   */
  showLoading$: Observable<boolean> = this.authService.isDoneLoading$.pipe(
    map((done) => !done),
    startWith(true)
  );

  /**
   * Constructor
   */
  constructor(
    public global: GlobalService,
    private router: Router,
    private metaService: HtmlMetaService,
    public modalService: ModalService,
    public authService: AuthenticationService,
    public userService: UserService,
    public currentUserStore: CurrentUserStore,
    public studentService: StudentService,
    private meta: Meta,
    private domain: DomainService,
    private locale: LocaleService,
    private renderer: Renderer2,
    // SentryFeedbackService injected to initialize user tracking
    _sentryFeedback: SentryFeedbackService,
    @Inject(DOCUMENT) private document: any
  ) {}

  /**
   * Ng on init
   */
  ngOnInit(): void {
    // Update meta titles when activating routes
    this.metaService.updateTitle();

    /**
     * Initializes the login flow
     */
    this.authService.runInitialLoginSequence();

    this.handleAuthenticatedUser();

    this.router.events.subscribe((event) => {
      if (event instanceof ActivationStart) {
        this.setMetaTags(event.snapshot.data);
      }
    });
  }

  /**
   * Handle authenticated user activity to set global data
   *
   * Set user roles / global account data
   */
  handleAuthenticatedUser(): void {
    this.authService.isDoneLoading$.subscribe((data) => {
      this.authService.isAuthenticated$.subscribe((authenticated) => {
        if (authenticated) {
          // Check initially 1 time to decide where to redirect if authenticated
          this.authService.profile$.subscribe((account) => {
            // Set user roles to service

            if (!empty(account) && account !== null) {
              if (account.hasOwnProperty("roles")) {
                this.userService.setCurrentUserRoles(account.roles);
                this.currentUserStore.setRoles(account.roles);

                // Check if we can get student profile data
                if (account.roles.includes(RoleTypes.Student)) {
                  this.studentService
                    .apiGetStudentprofileCurrent()
                    .pipe(
                      catchError(
                        this.studentService.handleError(
                          "apiGetStudentprofileCurrent",
                          false
                        )
                      )
                    )
                    .subscribe((studentprofile: any) => {
                      if (studentprofile) {
                        this.authService.setStudentprofileData(studentprofile);
                        this.currentUserStore.setProfileType(
                          studentprofile?.profileType ?? null
                        );
                      }
                    });
                }
              }

              // Set global user account data
              this.setGlobalAccountData(account);
            }
          });
        }
      });
    });
  }

  setGlobalAccountData(data: any): void {
    this.global.user = data;
  }

  handleError<T>(
    operation = "operation",
    result?: T
  ): (error: any) => Observable<T> {
    return (error: any): Observable<T> => {
      // Handle given operation
      switch (operation) {
        case "apiGetStudentprofileCurrent":
        case "apiGetCompanyCurrent":
          // Do not perform any action, profile could be non-existing
          break;

        default:
          this.modalService.Alert(
            "Error",
            "Something went wrong, please try again"
          );
          break;
      }

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  clearMetaTags(): void {
    this.meta.removeTag("description");
    this.meta.removeTag("keywords");
  }

  setMetaTags(data: any): void {
    this.clearMetaTags();

    const region = this.domain.getCurrentRegion();
    const language = this.locale.getCurrentLanguage();

    if (data) {
      let title = null;
      let metaDescription = null;
      let metaKeywords = null;

      if (data.hasOwnProperty("title")) {
        title = data.title;
      }

      if (data.hasOwnProperty("metaDescription")) {
        metaDescription = data.metaDescription;
      }
      if (data.hasOwnProperty("metaKeywords")) {
        metaKeywords = data.metaKeywords;
      }

      if (region === "BR" || language === "pt") {
        if (data.hasOwnProperty("metaDescriptionPt")) {
          metaDescription = data.metaDescriptionPt;
        }
        if (data.hasOwnProperty("metaKeywordsPt")) {
          metaKeywords = data.metaKeywordsPt;
        }
      } else if (region === "EU" || language == "en") {
        if (data.hasOwnProperty("metaDescriptionEn")) {
          metaDescription = data.metaDescriptionEn;
        }
        if (data.hasOwnProperty("metaKeywordsEn")) {
          metaKeywords = data.metaKeywordsEn;
        }
      } else if (region === "DE" || language == "de") {
        if (data.hasOwnProperty("metaDescriptionDe")) {
          metaDescription = data.metaDescriptionDe;
        }
        if (data.hasOwnProperty("metaKeywordsDe")) {
          metaKeywords = data.metaKeywordsDe;
        }
      }

      this.meta.updateTag({ name: "title", content: title ?? "" });
      this.meta.updateTag({
        name: "description",
        content: metaDescription ?? "",
      });
      this.meta.updateTag({ name: "keywords", content: metaKeywords ?? "" });
      this.meta.updateTag({ name: "og:image", content: "" }); // clear og by image default
    }

    this.setHrefTags(region);
  }

  setHrefTags(region: string): void {
    let currentHrefLang = "nl";

    if (region === "BR") {
      currentHrefLang = "pt";
    } else if (region === "EU") {
      currentHrefLang = "en";
    } else if (region === "DE") {
      currentHrefLang = "de";
    }

    const headChildren = Array.from(this.document.head.children);
    const linkEltFound = headChildren.find((e: any) => e.rel === "alternate");
    if (linkEltFound) {
      this.renderer.setAttribute(linkEltFound, "href", window.location.href);
      this.renderer.setAttribute(linkEltFound, "hreflang", currentHrefLang);
    }
  }
}
